import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors as uiSelectors } from '@elab/core/store/ui';
import { actions as authActions } from '@elab/core/store/auth';
import { actions as globalActions } from '@elab/core/store/global';

/**
 * Component is used to render the My Account navigation as a native dropdown in the mobile resolution.
 * Data is taken from 'ui.accountNav' store that are filled by
 * <e2-my-account-navigation-link>, <e2-my-account-logout> and <e2-my-account-navigation-group> components
 *
 * @author Roberto Schiavone
 *
 * HTML Structure
 * <e2-my-account-navigation></e2-my-account-navigation>
 *
 * @attr {String} [trackingId] - The name of the component which uses ScrollObserver
 * */
class MyAccountNavigation extends SimpleLitElement {
    static get properties() {
        return {
            trackingId: {
                type: String,
                attribute: 'tracking-id',
            },
            nav: {
                type: Array,
                attribute: false,
            },
            selectedOption: {
                type: String,
                attribute: false,
            },
            isSticky: {
                type: Boolean,
                attribute: false,
            },
        };
    }

    constructor() {
        super();
        this.nav = [];
        this.selectedOption = '';
        this.isSticky = false;
    }

    getNodeBy(name, value) {
        return this.nav.reduce((acc, node) => {
            if (acc) return acc;
            if (node.children) {
                return node.children.find(childNode => childNode[name] === value);
            }
            return node[name] === value ? node : null;
        }, null);
    }

    handleChange(e) {
        let selected = this.getNodeBy('title', e.target.value);
        if (selected.url) {
            this.dispatchEvent(
                new CustomEvent('redirect', {
                    detail: selected.url,
                }),
            );
        } else if (selected.eventName) {
            this.dispatchEvent(
                new CustomEvent(selected.eventName, {
                    detail: selected.redirectUrl,
                }),
            );
        }
    }

    renderSelectedOption() {
        if (this.selectedOption) {
            return this.selectedOption;
        }

        const category = this.nav && this.nav[0];
        const firstElement = category && category.children && category.children[0];

        return (firstElement && firstElement.title) || '';
    }

    render() {
        return html`
            <nav
                class="my-account-navigation ${this.isSticky
                    ? 'my-account-navigation__sticky'
                    : ''}"
            >
                <div class="my-account-navigation__select-wrapper">
                    <span class="my-account-navigation__selected-option">
                        ${this.renderSelectedOption()}
                    </span>
                    <select class="my-account-navigation__select" @change="${this.handleChange}">
                        ${this.nav.map(entry =>
                            entry.children
                                ? html`
                                      <optgroup label="${entry.title}">
                                          ${entry.children.map(
                                              child => html`
                                                  <option
                                                      ?selected=${this.selectedOption ===
                                                          child.title}
                                                      class="my-account-navigation__option"
                                                      value="${child.title}"
                                                  >
                                                      ${child.title}
                                                  </option>
                                              `,
                                          )}
                                      </optgroup>
                                  `
                                : html`
                                      <option
                                          ?selected=${this.selectedOption === entry.title}
                                          class="my-account-navigation__option"
                                          value="${entry.title}"
                                      >
                                          ${entry.title}
                                      </option>
                                  `,
                        )}
                    </select>
                    ${this.isSticky
                        ? html`
                              <i class="icon icon-home"></i>
                          `
                        : null}
                    <i class="my-account-navigation__select-icon"></i>
                </div>
            </nav>
        `;
    }
}

const mapStateToProps = (state, ctx) => ({
    nav: uiSelectors.getAccountNavigation(state),
    selectedOption: uiSelectors.getAccountNavigationActive(state).title || '',
    isSticky: uiSelectors.getVisibleElements(state).includes(ctx.trackingId),
});

const mapDispatchToEvents = dispatch => ({
    'account-navigation-logout': e => dispatch(authActions.doLogout(e.detail)),
    redirect: e => dispatch(globalActions.redirect(e.detail)),
});

export default () =>
    customElements.define(
        'e2-my-account-navigation',
        connect(mapStateToProps, mapDispatchToEvents)(MyAccountNavigation),
    );
